import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { fetchCmsList } from '../lib/cms';

export function BlogIndex() {
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Alle');

  useEffect(() => {
    async function load() {
      const data = await fetchCmsList('articles');
      if (data.length) {
        const sorted = [...data].sort((a, b) => new Date(b.date || '2026-01-01') - new Date(a.date || '2026-01-01'));
        setArticles(sorted);
      }
    }
    load();
  }, []);

  const categories = ['Alle', 'Fodpleje info', 'Gode råd', 'Sygdomme & Gener', 'Behandlinger'];

  const filteredArticles = articles.filter((art) => {
    const matchesSearch = 
      art.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      art.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      art.content?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = 
      selectedCategory === 'Alle' || 
      art.category?.toLowerCase() === selectedCategory.toLowerCase() ||
      (selectedCategory === 'Gode råd' && (art.title?.includes('råd') || art.title?.includes('fejl') || art.title?.includes('sko'))) ||
      (selectedCategory === 'Sygdomme & Gener' && (art.title?.includes('ligtorne') || art.title?.includes('negle') || art.title?.includes('hælrevner'))) ||
      (selectedCategory === 'Behandlinger' && (art.title?.includes('laser') || art.title?.includes('hjemme') || art.title?.includes('fodterapeut')));

    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <SEO 
        title="Blog & Viden om Fodpleje | Fodtid"
        description="Læs 19 faglige artikler, guides og gode råd om fødder, ligtorne, indgroede negle, hælrevner, sko, laserbehandling og mobil fodpleje."
      />

      {/* Header */}
      <section className="bg-canvas py-14 sm:py-18 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center space-y-3">
          <span className="text-xs uppercase font-bold tracking-widest text-brand-charcoal bg-surface px-3.5 py-1 rounded-full border border-brand-border">
            Faglige Guides & Inspiration
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium uppercase tracking-wide text-brand-charcoal">
            Blog & Videnscenter
          </h1>
          <p className="text-brand-muted text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Her finder du faglige råd, forklaringer og vejledning til, hvordan du passer bedst på dine fødder og undgår unødige smerter i hverdagen.
          </p>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <section className="hidden min-[1200px]:block py-6 bg-surface/40 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            
            {/* Category tabs */}
            <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all focus:outline-none ${
                    selectedCategory === cat
                      ? 'bg-brand-charcoal text-canvas shadow-xs'
                      : 'bg-surface text-brand-charcoal hover:bg-surface-soft border border-brand-border'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <input
                type="text"
                placeholder="Søg i artikler..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-full border border-brand-border bg-canvas text-xs focus:outline-none focus:ring-2 focus:ring-brand-accent focus:bg-canvas transition-all text-brand-charcoal placeholder:text-brand-muted"
              />
              <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-muted text-xs" aria-hidden="true" />
            </div>

          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 sm:py-24 bg-canvas border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16 space-y-4">
              <div className="w-16 h-16 rounded-full bg-surface border border-brand-border text-brand-muted flex items-center justify-center text-2xl mx-auto">
                <i className="fa-regular fa-folder-open" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-xl font-bold text-brand-charcoal">
                Ingen artikler matchede din søgning
              </h3>
              <p className="text-brand-muted text-xs">
                Prøv at søge efter andre ord eller vælg en anden kategori.
              </p>
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setSelectedCategory('Alle'); }}
                className="inline-flex items-center gap-2 text-xs font-semibold text-brand-charcoal bg-surface border border-brand-border px-4 py-2 rounded-full hover:bg-surface-soft transition-colors"
              >
                Nulstil filtre
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((art) => {
                const articleSlug = typeof art.slug === 'object' ? (art.slug?.da || Object.values(art.slug || {})[0] || art.id) : (art.slug || art.id);
                const displayDate = art.date || art.publish_date || "2026";
                
                return (
                  <Link
                    key={art.id || articleSlug}
                    to={`/blog/${articleSlug}`}
                    className="bg-surface/50 rounded-3xl p-6 sm:p-7 border border-brand-border shadow-xs hover:border-brand-accent hover:shadow-md transition-all flex flex-col justify-between group block"
                  >
                    <div>
                      {art.image?.[0] && (
                        <div className="mb-5 aspect-4/3 rounded-2xl overflow-hidden bg-[#FAF7F2] border border-brand-border/60 flex items-center justify-center p-2.5 group-hover:border-brand-accent/50 transition-colors">
                          <img
                            src={art.image[0].includes('assets.auxodev.dk') && !art.image[0].includes('/cdn-cgi/image/')
                              ? art.image[0].replace('assets.auxodev.dk/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/')
                              : art.image[0]}
                            alt={art.title}
                            className="w-full h-full object-contain group-hover:scale-102 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-[11px] text-brand-muted font-medium flex-wrap">
                          <span className="text-brand-charcoal font-semibold bg-surface px-2.5 py-0.5 rounded-full border border-brand-border">
                            {art.category || 'Fodpleje info'}
                          </span>
                          <span>{displayDate}</span>
                          <span>•</span>
                          <span>{art.author || "Klaus Just"}</span>
                        </div>

                        <h2 className="font-serif text-lg font-bold text-brand-charcoal group-hover:text-brand-accent transition-colors line-clamp-2">
                          {art.title}
                        </h2>

                        <p className="text-xs text-brand-muted leading-relaxed line-clamp-3">
                          {art.summary || art.excerpt}
                        </p>
                      </div>
                    </div>

                    <div className="pt-6">
                      <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-brand-charcoal group-hover:text-brand-accent transition-colors">
                        <span>Læs hele artiklen</span>
                        <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

        </div>
      </section>
    </>
  );
}
